import { memo } from "react";
import { useSelector } from "react-redux";

import { KeyboardDoubleArrowLeftRounded } from "@mui/icons-material";
import Box from "@mui/material/Box";
import EditableDataRow from "../../components/common/EditableDataRow";
import RffLoanDataGroup from "../../components/finalForm/shared/RffLoanDataGroup";
import Flx from "../../components/layout/Flx";
import TitledGroup from "../../components/ui/TitledGroup";
import LoanTotalsDonutChart from "./shared/LoanTotalsDonutChart";
import DrilldownSidebarPane from "./sidebar/DrilldownSidebarPane";
const CELL_VALUE_SIZE = 5;
const LoanDrilldownTotalsLeftSidebar = memo(({ show, onClose, onOpen }) => {
  if (!show) return;

  return (
    <DrilldownSidebarPane
      initialWidth="295px"
      title="Loan Totals"
      onClose={onClose}
      onCloseIcon={<KeyboardDoubleArrowLeftRounded />}
    >
      <LoanTotals />
    </DrilldownSidebarPane>
  );
});

const LoanTotals = ({ children }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  return (
    <TitledGroup variant="h4" suppressPaddingTopBottom>
      <Box sx={{ width: "100%", overflow: "hidden", mb: 2 }}>
        <LoanTotalsDonutChart loanData={loanDrilldown} />
      </Box>
      <Flx column>
        <ConditionalLogicField />
        <ConstantFields />
      </Flx>
    </TitledGroup>
  );
};

const ConditionalLogicField = ({ children }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  return (
    <>
      <RffLoanDataGroup column initialValues={loanDrilldown}>
        <EditableDataRow
          name="totalLoanAmount"
          label="Total Loan Amount"
          type="dollar"
          disabled
          data={loanDrilldown}
          cellValueSize={CELL_VALUE_SIZE}
          condition={{
            operator: "OR",
            rules: [
              // Fix and Flip (all 4)
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Fix and Flip",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // Ground Up Construction
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Ground Up Construction",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // Stabilized Bridge
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Stabilized Bridge",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // DSCR
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "DSCR",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // Vacant Land
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Vacant Land",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // Other
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Other",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // Foreclosure Bail Out (always refi)
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Foreclosure Bail Out",
                  },
                ],
              },
            ],
          }}
        />

        <EditableDataRow
          name="totalPurchasePrice"
          label="Total Purchase Price"
          type="dollar"
          disabled
          data={loanDrilldown}
          cellValueSize={CELL_VALUE_SIZE}
          condition={{
            operator: "OR",
            rules: [
              // Fix and Flip
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Fix and Flip",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: ["Purchase", "Delayed Purchase"],
                  },
                ],
              },
              // Ground Up Construction
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Ground Up Construction",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: ["Purchase", "Delayed Purchase"],
                  },
                ],
              },
              // Stabilized Bridge
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Stabilized Bridge",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: ["Purchase", "Delayed Purchase"],
                  },
                ],
              },
              // DSCR
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "DSCR",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: ["Purchase", "Delayed Purchase"],
                  },
                ],
              },
              // Vacant Land
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Vacant Land",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: ["Purchase", "Delayed Purchase"],
                  },
                ],
              },
              // Other
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Other",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: ["Purchase", "Delayed Purchase"],
                  },
                ],
              },
            ],
          }}
        />

        <EditableDataRow
          name="totalAsIsValue"
          label="Total As Is Value"
          type="dollar"
          disabled
          data={loanDrilldown}
          cellValueSize={CELL_VALUE_SIZE}
          condition={{
            operator: "OR",
            rules: [
              // Fix and Flip (all 4 purchase/refi/cash/delayed)
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Fix and Flip",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // Ground Up Construction (all 4)
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Ground Up Construction",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // Stabilized Bridge (all 4)
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Stabilized Bridge",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // DSCR (all 4)
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "DSCR",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // Vacant Land (all 4)
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Vacant Land",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // Other (all 4)
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Other",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // Foreclosure Bail Out => always refi
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Foreclosure Bail Out",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: ["Refinance - No Cash Out", "Refinance - Cash Out"],
                  },
                ],
              },
            ],
          }}
        />

        <EditableDataRow
          name="totalSecondaryAsIsValue"
          label="Total Secondary As-Is Value"
          type="dollar"
          disabled
          data={loanDrilldown}
          cellValueSize={CELL_VALUE_SIZE}
          condition={{
            operator: "OR",
            rules: [
              // Fix and Flip (all 4 purchase/refi/cash/delayed)
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Fix and Flip",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // Ground Up Construction (all 4)
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Ground Up Construction",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // Stabilized Bridge (all 4)
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Stabilized Bridge",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // DSCR (all 4)
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "DSCR",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // Vacant Land (all 4)
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Vacant Land",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // Other (all 4)
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Other",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // Foreclosure Bail Out => always refi
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Foreclosure Bail Out",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: ["Refinance - No Cash Out", "Refinance - Cash Out"],
                  },
                ],
              },
            ],
          }}
        />

        <EditableDataRow
          name="totalArv"
          label="Total ARV"
          type="dollar"
          disabled
          data={loanDrilldown}
          cellValueSize={CELL_VALUE_SIZE}
          condition={{
            operator: "OR",
            rules: [
              // Fix and Flip + all 4 loanPurpose
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Fix and Flip",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // Ground Up Construction + all 4 loanPurpose
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Ground Up Construction",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
            ],
          }}
        />

        <EditableDataRow
          name="totalSecondaryArv"
          label="Total Secondary ARV"
          type="dollar"
          disabled
          data={loanDrilldown}
          cellValueSize={CELL_VALUE_SIZE}
          condition={{
            operator: "OR",
            rules: [
              // Fix and Flip + all 4 loanPurpose
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Fix and Flip",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // Ground Up Construction + all 4 loanPurpose
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Ground Up Construction",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
            ],
          }}
        />

        <EditableDataRow
          name="totalCostBasis"
          label="Total Cost Basis"
          type="dollar"
          disabled
          data={loanDrilldown}
          cellValueSize={CELL_VALUE_SIZE}
          condition={{
            operator: "OR",
            rules: [
              // Fix and Flip
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Fix and Flip",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // Ground Up Construction
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Ground Up Construction",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
            ],
          }}
        />

        <EditableDataRow
          name="totalConstructionBudget"
          label="Total Construction Budget"
          type="dollar"
          disabled
          data={loanDrilldown}
          cellValueSize={CELL_VALUE_SIZE}
          condition={{
            operator: "AND",
            rules: [
              {
                field: "loanProductType",
                operator: "==",
                value: "Ground Up Construction",
              },
              {
                field: "loanPurpose",
                operator: "includes",
                value: [
                  "Purchase",
                  "Refinance - No Cash Out",
                  "Refinance - Cash Out",
                  "Delayed Purchase",
                ],
              },
            ],
          }}
        />

        <EditableDataRow
          name="outstandingLoanUpb"
          label="Outstanding Loan UPB"
          type="dollar"
          disabled
          data={loanDrilldown}
          cellValueSize={CELL_VALUE_SIZE}
          condition={{
            operator: "OR",
            rules: [
              // F&F Refi
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Fix and Flip",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: ["Refinance - No Cash Out", "Refinance - Cash Out"],
                  },
                ],
              },
              // GUC Refi
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Ground Up Construction",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: ["Refinance - No Cash Out", "Refinance - Cash Out"],
                  },
                ],
              },
              // Stabilized Bridge Refi
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Stabilized Bridge",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: ["Refinance - No Cash Out", "Refinance - Cash Out"],
                  },
                ],
              },
              // DSCR Refi
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "DSCR",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: ["Refinance - No Cash Out", "Refinance - Cash Out"],
                  },
                ],
              },
              // Vacant Land Refi
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Vacant Land",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: ["Refinance - No Cash Out", "Refinance - Cash Out"],
                  },
                ],
              },
              // Other Refi
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Other",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: ["Refinance - No Cash Out", "Refinance - Cash Out"],
                  },
                ],
              },
              // Foreclosure Bail Out => always refi, so just match product type
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Foreclosure Bail Out",
                  },
                ],
              },
            ],
          }}
        />

        <EditableDataRow
          name="totalAnnualInsurance"
          label="Total Annual Insurance"
          type="dollar"
          disabled
          data={loanDrilldown}
          cellValueSize={CELL_VALUE_SIZE}
          condition={{
            operator: "OR",
            rules: [
              // Stabilized Bridge
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Stabilized Bridge",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // DSCR
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "DSCR",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // Vacant Land
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Vacant Land",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // Other
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Other",
                  },
                  {
                    field: "loanPurpose",
                    operator: "includes",
                    value: [
                      "Purchase",
                      "Refinance - No Cash Out",
                      "Refinance - Cash Out",
                      "Delayed Purchase",
                    ],
                  },
                ],
              },
              // Foreclosure Bail Out => always refi
              {
                operator: "AND",
                rules: [
                  {
                    field: "loanProductType",
                    operator: "==",
                    value: "Foreclosure Bail Out",
                  },
                ],
              },
            ],
          }}
        />
      </RffLoanDataGroup>
    </>
  );
};

const ConstantFields = ({ children }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  return (
    <>
      <Flx column initialValues={loanDrilldown}>
        <EditableDataRow
          data={loanDrilldown}
          cellValueSize={CELL_VALUE_SIZE}
          placeholder={"—"}
          disabled={true}
          name="totalAssignmentFees"
          label="Total Assignment Fees"
          type="dollar"
        />
        <EditableDataRow
          data={loanDrilldown}
          cellValueSize={CELL_VALUE_SIZE}
          placeholder={"—"}
          disabled={true}
          name="totalAnnualTaxes"
          label="Total Annual Taxes"
          type="dollar"
        />

        <EditableDataRow
          data={loanDrilldown}
          cellValueSize={CELL_VALUE_SIZE}
          placeholder={"—"}
          disabled={true}
          name="totalAnnualFloodInsurance"
          label="Total Annual Flood Insurance"
          type="dollar"
        />
        <EditableDataRow
          data={loanDrilldown}
          cellValueSize={CELL_VALUE_SIZE}
          placeholder={"—"}
          disabled={true}
          name="totalAnnualHoaFees"
          label="Total Annual HOA Fees"
          type="dollar"
        />
        <EditableDataRow
          data={loanDrilldown}
          cellValueSize={CELL_VALUE_SIZE}
          placeholder={"—"}
          disabled={true}
          name="totalAnnualPropertyMgmtFees"
          label="Total Annual Property Mgmt Fees"
          type="dollar"
        />
        <EditableDataRow
          data={loanDrilldown}
          cellValueSize={CELL_VALUE_SIZE}
          placeholder={"—"}
          disabled={true}
          name="totalAnnualOtherExpense"
          label="Total Annual Other Expense"
          type="dollar"
        />
        <EditableDataRow
          data={loanDrilldown}
          cellValueSize={CELL_VALUE_SIZE}
          placeholder={"—"}
          disabled={true}
          name="totalConstructionBudget"
          label="Total Construction Budget"
          type="dollar"
          suppressBottomBorder
        />
      </Flx>
    </>
  );
};

export default LoanDrilldownTotalsLeftSidebar;
